import { useState, useEffect, useId, useRef } from 'react';
import { useModalFocus } from '../hooks/useModalFocus';
import { X, Search, SlidersHorizontal, Sparkles, ChevronRight } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { apiGet } from '../utils/apiClient';
import { normalizeRecipeSummary } from '../utils/normalizeRecipeDetail';
import { effectiveTotalMinutes, formatMinutes } from '../utils/recipeTime';

// Simple filter modal specifically for swap functionality
function SwapFilterModal({
  isOpen,
  onClose,
  onApplyFilters,
  initialFilters,
  isLargeScreen = false,
}) {
  const [tempFilters, setTempFilters] = useState(initialFilters);

  const panelRef = useRef(null);
  const titleId = useId();
  useModalFocus({ isOpen, panelRef, onClose });

  if (!isOpen) return null;

  const handleApply = () => {
    onApplyFilters(tempFilters);
    onClose();
  };

  const handleClearAll = () => {
    setTempFilters({
      mealType: [],
      dietaryTags: [],
      prepTime: 'any',
    });
  };

  const toggleMealType = (type) => {
    setTempFilters(prev => ({
      ...prev,
      mealType: prev.mealType.includes(type)
        ? prev.mealType.filter(t => t !== type)
        : [...prev.mealType, type],
    }));
  };

  const toggleDietaryTag = (tag) => {
    setTempFilters(prev => ({
      ...prev,
      dietaryTags: prev.dietaryTags.includes(tag)
        ? prev.dietaryTags.filter(t => t !== tag)
        : [...prev.dietaryTags, tag],
    }));
  };

  return (
    <div className="mpg-focus-scope fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center sm:justify-center">
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} 
        className="bg-card text-foreground w-full mx-auto rounded-t-3xl sm:rounded-3xl max-h-[80vh] flex flex-col shadow-[0_-8px_24px_rgba(0,0,0,0.08)]"
        style={{
          maxWidth: isLargeScreen ? '600px' : '448px',
          transition: 'max-width 0.3s ease'
        }}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-[var(--border)] flex items-center justify-between">
          <h2 id={titleId} className="text-xl">Filters</h2>
          <button
            onClick={onClose}
            className="w-11 h-11 rounded-full hover:bg-gray-50 flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {/* Meal Type */}
          <div className="mb-6">
            <h3 className="text-xl">Meal Type</h3>
            <div className="flex flex-wrap gap-2">
              {['breakfast', 'lunch', 'dinner'].map((type) => (
                <button
                  key={type}
                  onClick={() => toggleMealType(type)}
                  className={`px-4 py-2 rounded-full text-sm transition-colors ${
                    tempFilters.mealType.includes(type)
                      ? 'bg-[var(--accent)] text-[var(--accent-foreground)]'
                      : 'bg-muted text-foreground hover:bg-[var(--primary-light)]'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Dietary Tags */}
          <div className="mb-6">
            <h3 className="text-xl">Dietary</h3>
            <div className="flex flex-wrap gap-2">
              {['Vegetarian', 'Vegan', 'Gluten-free', 'High Protein', 'Low Carb'].map((tag) => (
                <button
                  key={tag}
                  onClick={() => toggleDietaryTag(tag)}
                  className={`px-4 py-2 rounded-full text-sm transition-colors ${
                    tempFilters.dietaryTags.includes(tag)
                      ? 'bg-[var(--accent)] text-[var(--accent-foreground)]'
                      : 'bg-muted text-foreground hover:bg-[var(--primary-light)]'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Prep Time */}
          <div>
            <h3 className="text-xl">Total Time</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'any', label: 'Any' },
                { value: 'under-15', label: 'Under 15 min' },
                { value: '15-30', label: '15-30 min' },
                { value: '30-60', label: '30-60 min' },
                { value: 'over-60', label: 'Over 60 min' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTempFilters(prev => ({ ...prev, prepTime: option.value }))}
                  className={`px-4 py-2 rounded-full text-sm transition-colors ${
                    tempFilters.prepTime === option.value
                      ? 'bg-[var(--accent)] text-[var(--accent-foreground)]'
                      : 'bg-muted text-foreground hover:bg-[var(--primary-light)]'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--border)] flex gap-3">
          <button
            onClick={handleClearAll}
            className="flex-1 py-3 rounded-full border-2 border-[var(--primary)] text-[var(--primary-foreground)] bg-card hover:bg-[var(--primary-light)] transition-colors"
          >
            Clear All
          </button>
          <button
            onClick={handleApply}
            className="flex-1 py-3 rounded-full bg-[var(--primary)] text-[var(--primary-foreground)] hover:bg-[var(--primary-hover)] transition-colors"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
}

export function SwapMealModal({
  isOpen,
  onClose,
  mealType,
  currentMealId,
  currentRecipeId,
  currentMealName,
  onSwap,
  onAutoGenerate,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    mealType: [mealType.toLowerCase()],
    dietaryTags: [],
    prepTime: 'any',
  });
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [allRecipes, setAllRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Same behaviour as Add meal: Auto Generate waits for the user's recipes, and says so.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [swapping, setSwapping] = useState(false);
  const [swapMessage, setSwapMessage] = useState('');

  // Responsive screen detection
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px)');
    
    const handleMediaChange = (e) => {
      setIsLargeScreen(e.matches);
    };
    
    // Set initial value
    setIsLargeScreen(mediaQuery.matches);
    
    // Listen for changes
    mediaQuery.addEventListener('change', handleMediaChange);
    
    // Cleanup
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  // Normalize backend recipe shape (image handling consistent with Recipes.jsx)
  // Load recipes from API when modal opens
  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        setLoading(true);
        setError(null);
        setHasLoaded(false);
        setSwapMessage('');
        const docs = await apiGet('recipes');
        const normalized = (Array.isArray(docs) ? docs : []).map(normalizeRecipeSummary);
        if (alive) {
          setAllRecipes(normalized);
          setHasLoaded(true);
        }
      } catch (e) {
        if (alive) setError(e);
      } finally {
        if (alive) setLoading(false);
      }
    }
    if (isOpen) load();
    return () => { alive = false; };
  }, [isOpen]);

  const panelRef = useRef(null);
  const titleId = useId();
  // Paused while the Filters pop-up is open on top, so that one keeps the keyboard.
  useModalFocus({ isOpen, panelRef, onClose, paused: isFilterModalOpen });

  if (!isOpen) return null;


  // Filter recipes based on meal type, search query, and filters
  const filteredRecipes = allRecipes.filter((recipe) => {
    // Filter by meal type
    const matchesMealType = selectedFilters.mealType.length === 0 || 
      selectedFilters.mealType.includes(recipe.mealType);
    
    // Filter by search query
    const matchesSearch = recipe.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      recipe.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    // Filter by dietary tags
    const matchesDietaryTags = selectedFilters.dietaryTags.length === 0 ||
      selectedFilters.dietaryTags.every(filterTag => 
        recipe.tags.some(recipeTag => recipeTag.toLowerCase() === filterTag.toLowerCase())
      );
    
    // Filter by prep time
    // null total = no time recorded, so it can't satisfy any band. Previously an untimed
    // recipe summed to 0 and wrongly matched "under 15".
    const total = effectiveTotalMinutes(recipe);
    const matchesPrepTime = selectedFilters.prepTime === 'any' || (total !== null && (
      (selectedFilters.prepTime === 'under-15' && total <= 15) ||
      (selectedFilters.prepTime === '15-30' && total > 15 && total <= 30) ||
      (selectedFilters.prepTime === '30-60' && total > 30 && total <= 60) ||
      (selectedFilters.prepTime === 'over-60' && total > 60)));

    return matchesMealType && matchesSearch && matchesDietaryTags && matchesPrepTime;
  });

  const handleAutoGenerate = () => {
    if (!hasLoaded || swapping) return; // disabled until the user's recipes are in
    const forMealType = (list) => list.filter((r) => r.mealType === mealType.toLowerCase());
    // Prefer the current filters; fall back to all of the user's recipes for this meal type.
    let pool = forMealType(filteredRecipes);
    if (!pool.length) pool = forMealType(allRecipes);
    // Never "swap" to the same recipe when there's anything else to offer.
    const others = pool.filter((r) => String(r.id) !== String(currentRecipeId || ''));
    if (others.length) pool = others;
    if (!pool.length) {
      setSwapMessage(`You don't have any ${mealType.toLowerCase()} recipes yet, so there's nothing to suggest. Add one in Recipes, then try again.`);
      return;
    }
    if (pool.length === 1 && String(pool[0].id) === String(currentRecipeId || '')) {
      setSwapMessage(`This is your only ${mealType.toLowerCase()} recipe, so there's nothing to swap it for yet.`);
      return;
    }
    handleSelectRecipe(pool[Math.floor(Math.random() * pool.length)].id);
  };

  // Waits for the swap; the meal planner reports any failure in its own error box.
  const handleSelectRecipe = async (recipeId) => {
    setSwapMessage('');
    setSwapping(true);
    try {
      await onSwap(recipeId);
      onClose();
    } finally {
      setSwapping(false);
    }
  };


  const handleApplyFilters = (filters) => {
    setSelectedFilters(filters);
    setIsFilterModalOpen(false);
  };

  const activeFilterCount = 
    (selectedFilters.mealType.length > 1 ? selectedFilters.mealType.length - 1 : 0) + // -1 because current meal type is default
    selectedFilters.dietaryTags.length +
    (selectedFilters.prepTime !== 'any' ? 1 : 0);

  return (
    <>
      <div className="mpg-focus-scope fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center sm:justify-center">
        <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} 
          className="bg-card text-foreground w-full mx-auto rounded-t-3xl sm:rounded-3xl max-h-[90vh] flex flex-col shadow-[0_-8px_24px_rgba(0,0,0,0.08)]"
          style={{
            maxWidth: isLargeScreen ? '700px' : '448px',
            transition: 'max-width 0.3s ease'
          }}
        >
          {/* Header */}
          <div className="bg-card border-b border-[var(--border)] rounded-t-3xl sm:rounded-t-3xl px-6 py-4 flex items-center justify-between flex-shrink-0">
            <div>
              <h2 id={titleId} className="text-xl">Swap Meal</h2>
              <p className="text-sm text-muted-foreground mt-1">Currently: {currentMealName}</p>
            </div>
            <button
              onClick={onClose}
              className="w-11 h-11 rounded-full hover:bg-gray-50 flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          {/* Auto Generate Option */}
          <div className="px-6 pt-6 pb-4 flex-shrink-0">
            <button
              type="button"
              onClick={handleAutoGenerate}
              disabled={!hasLoaded || swapping}
              aria-busy={loading || swapping || undefined}
              className="disabled:cursor-wait disabled:opacity-70 w-full py-2 px-6 bg-[var(--accent)] text-[var(--accent-foreground)] rounded-2xl shadow-md hover:bg-[var(--accent-hover)] transition-colors flex items-center justify-between group"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center text-xl">
                  <Sparkles className="w-4 h-4 text-[var(--accent-foreground)]" />
                </div>
                <div className="text-left leading-tight translate-y-[4px]">
                  <p className="font-medium text-md text-[var(--accent-foreground)] leading-snug">Auto Generate</p>
                  <p className="text-sm text-[color-mix(in_srgb,_var(--accent-foreground)_75%,_transparent_25%)] leading-tight">{loading || !hasLoaded ? 'Loading your recipes…' : swapping ? 'Swapping…' : 'Get a new suggestion'}</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-[var(--accent-foreground)] group-hover:translate-x-1 transition-transform" />
            </button>
            {/* Always mounted so a new message is announced. */}
            <div role="alert">
              {swapMessage && (
                <p className="mt-3 rounded-xl border border-[#D4183D] bg-[#FEF3F2] px-3 py-2 text-sm text-[#B42318]">{swapMessage}</p>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="px-6 flex items-center gap-4 mb-4 flex-shrink-0">
            <div className="flex-1 h-px bg-muted" />
            <span className="text-sm text-muted-foreground">or search recipes</span>
            <div className="flex-1 h-px bg-muted" />
          </div>

          {/* Search and Filter */}
          <div className="px-6 mb-4 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search recipes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-card rounded-full border border-[var(--border)] text-foreground"
                />
              </div>
              <button
                onClick={() => setIsFilterModalOpen(true)}
                className="relative w-12 h-12 rounded-full bg-card border border-[var(--border)] hover:bg-gray-50 flex items-center justify-center transition-colors flex-shrink-0"
                aria-label="Filter"
              >
                <SlidersHorizontal className="w-5 h-5 text-muted-foreground" />
                {activeFilterCount > 0 && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-xs">
                    {activeFilterCount}
                  </div>
                )}
              </button>
            </div>
          </div>

          {/* Recipe List */}
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            {loading ? (
              <div className="text-center py-12"><p className="text-muted-foreground">Loading recipes…</p></div>
            ) : error ? (
              <div className="text-center py-12"><p className="text-muted-foreground">Failed to load recipes</p></div>
            ) : filteredRecipes.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No recipes found</p>
                <p className="text-sm text-muted-foreground mt-2">Try adjusting your search or filters</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredRecipes.map((recipe) => (
                 <div
                    key={recipe.id}
                    className="bg-card rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow border border-[var(--border)]"
                  >
                    {/* Recipe Name - Full Width */}
                    <h2 className="text-xl font-medium">{recipe.name}</h2>
                    
                    {/* Image, Details, and Button - Second Row */}
                    <div className="flex items-center gap-4">
                      <div className="w-20 h-20 rounded-2xl overflow-hidden flex-shrink-0">
                        <ImageWithFallback
                          src={recipe.imageThumb || recipe.image}
                          fallbackSrc={recipe.image}
                          alt={recipe.name}
                          loading="lazy"
                          decoding="async"
                          fetchPriority="low"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap gap-1 mb-2">
                          {recipe.tags.slice(0, 2).map((tag) => (
                            <span
                              key={tag}
                              className="px-2 py-0.5 bg-muted text-muted-foreground rounded-full text-xs"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formatMinutes(effectiveTotalMinutes(recipe))}
                      
                        </p>
                      </div>

                      <button
                        onClick={() => handleSelectRecipe(recipe.id)}
                        disabled={swapping}
                        className="disabled:opacity-60 px-4 py-2 bg-primary text-primary-foreground rounded-full hover:bg-primary-hover transition-colors text-sm flex-shrink-0"
                      >
                        Select
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      <SwapFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        onApplyFilters={handleApplyFilters}
        initialFilters={selectedFilters}
        isLargeScreen={isLargeScreen}
      />
    </>
  );
}

// Removed static mock data; recipes now fetched from API via apiClient
